// src/components/desk/ClientSharePanel.jsx — who works this client, and the two ways that changes.
//
// Owner, 2026-09-02: *"Opting out mean giving away as lead but sharing mean they stay and add
// someone else too."* Two verbs, and the difference is the whole component:
//
//   SHARE   you keep the client AND a colleague gets it too. Both of you represent them, so the
//           colleague sees exactly what you see — contact details included — because they ARE a
//           representative, not because a permission list says so.
//   LEAVE   you stop representing them. If a colleague still does, it is theirs now. If you were
//           the last, the client goes back to the pool for anyone to pick up.
//
// ⚠️ NOT the meeting modal's ClientRepPickPanel, which looks similar and does the opposite: that
// one HANDS OVER (`p_replace: true`), ending everyone else's representation and revoking the
// grants they gave. Sharing adds; handing over replaces. Two panels because two acts.
//
// ⚠️ LEAVING IS NOT SYMMETRICAL WITH SHARING and the copy must not pretend it is. Sharing is
// reversible by the person who received it (they can leave); leaving is not reversible by the
// person who did it — once you are out, getting back in means being shared to, or claiming from
// the pool if nobody took it. So the leave button asks, and asks with the consequence named,
// which is why the confirmation text differs depending on whether anyone else is left.
import { useEffect, useState } from 'react'
import { t, tLog } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { btn, pill } from './primitives.jsx'
// The leave decision lives in lib/ and is tested there — see its header for why.
import { leaveOutcome } from '../../lib/clientShare.js'
import { AgentPicker } from './AgentPicker.jsx'


export function ClientSharePanel({
  extraActions = null,    // rendered in the action row after share / leave (the Telegram lead alert)
  representatives = [],   // [{ party_id, agent_id, name }] — who represents this client now
  agents = [],            // the roster, for the add picker
  myPartyId = null,
  busy = false,
  error = null,
  onShare,                // (agent) => void
  onLeave,                // () => void
  onUnshare,              // (rep) => void — stop a share YOU made; the ✕ on a colleague's chip
  canShare = true,
  // A counter the client modal's ↗ pill bumps to open the picker from the footer (owner
  // 2026-09-08: "on client modal just add share pill, no link pill"). A SIGNAL rather than a
  // boolean, so pressing the pill again re-opens a picker the agent has since cancelled — a
  // controlled boolean would sit at true and the second press would do nothing visible.
  openSignal = 0,
}) {
  const [confirmLeave, setConfirmLeave] = useState(false)
  const [picking, setPicking] = useState(false)
  // ── ONE, MANY, OR ALL (owner 2026-09-08) ────────────────────────────────────────────────
  // A tap used to share immediately and close the picker, so "give this to the three people
  // covering Saburtalo" meant reopening it three times. Tapping now SELECTS; one button
  // shares the set. `ყველა` selects everyone the list is currently offering — deliberately
  // the filtered list and never the whole roster, so the count on the button is always the
  // set the agent can see, not one it is only promising.
  useEffect(() => { if (!busy) setPicking(false) }, [busy])
  // Skips the initial 0 — mounting the panel must not spring the picker open by itself.
  useEffect(() => { if (openSignal > 0 && canShare) setPicking(true) }, [openSignal])   // eslint-disable-line react-hooks/exhaustive-deps

  const mine = representatives.some(r => r.party_id && r.party_id === myPartyId)
  const outcome = leaveOutcome(representatives, myPartyId)
  const others = outcome.others
  // Anyone ALREADY representing is excluded by the picker — offering them again would
  // present "add" for a state that is already true.
  const held = new Set(representatives.map(r => r.agent_id).filter(Boolean))

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-semibold text-muted-foreground">{t('desk_client_reps_heading')}</span>
        {representatives.length === 0 && (
          // ⚠️ Said out loud rather than shown as an empty row: no representative IS the
          // client-lead pool state, and an agent looking at a blank space cannot tell that from
          // "still loading".
          <span className="text-xs text-amber-600 dark:text-amber-400">
            <Icon name="alert-triangle" size={12} /> {t('desk_client_reps_none')}
          </span>
        )}
        {representatives.map(r => (
          <span key={r.party_id || r.agent_id} data-rep={r.party_id || r.agent_id}
            className={pill('none', { extra: r.party_id === myPartyId
              ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 font-semibold'
              : 'bg-muted/50' })}>
            {r.name || t('desk_client_rep_unnamed')}
            {r.party_id === myPartyId && <span className="ml-1 opacity-70">{t('desk_client_rep_you')}</span>}
            {/* ── STOP SHARING (owner 2026-09-08: "need to have stop sharing") ──────────
                Sharing had no undo: a client shared with fifteen colleagues — one tap, with
                All — could only be unwound by asking each of them to press Leave.
                ⚠️ THE ✕ IS DRAWN FROM THE SERVER'S `can_remove`, never from a rule guessed
                here. The rule is "undo your own act, or be management", and a client that
                re-derived it would eventually disagree with the route and offer a control
                that 403s. A row whose grantor was never recorded is management-only, and it
                simply has no ✕ — the honest shape, rather than a disabled one that invites
                a press.
                ⚠️ Never on your OWN chip: leaving is a different act with a different
                consequence (it can put the client back in the pool) and its confirmation
                says which happened. */}
            {r.can_remove && onUnshare && (
              <button type="button" data-unshare={r.agent_id || r.party_id} disabled={busy}
                title={tLog('desk_client_unshare_title', { name: r.name || '' })}
                className="ml-1 opacity-60 hover:opacity-100 disabled:opacity-30"
                onClick={() => onUnshare(r)}>✕</button>
            )}
          </span>
        ))}
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className="flex flex-wrap items-center gap-2">
        {canShare && !picking && (
          <button type="button" className={btn('ghost')} disabled={busy} onClick={() => setPicking(true)}>
            <Icon name="user-plus" size={13} /> {t('desk_client_share')}
          </button>
        )}
        {/* Only offered to someone who actually holds it — a button that would 403 is a lock
            naming an action the viewer does not have. */}
        {mine && !confirmLeave && (
          <button type="button" className={btn('ghost')} disabled={busy}
            onClick={() => setConfirmLeave(true)}>
            <Icon name="log-out" size={13} /> {t('desk_client_leave')}
          </button>
        )}
        {extraActions}
      </div>

      {picking && (
        // ⚠️ ONE PICKER, TWO PRODUCTS. Extracted 2026-09-08 so the property share uses the
        // identical control — every rule inside it (the visible All row, All meaning the
        // filtered set, the toggle, the disabled empty commit) was paid for here, and a copy
        // would mean paying for each of them again on the other side.
        <AgentPicker agents={agents} exclude={[...held]} busy={busy}
          note={t('desk_client_share_note')}
          onCommit={list => onShare?.(list)}
          onCancel={() => setPicking(false)} />
      )}

      {confirmLeave && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-2 space-y-1.5">
          {/* ⚠️ THE TWO OUTCOMES ARE DIFFERENT FACTS and the copy has to say which one this is.
              Leaving a client a colleague also works hands it to them. Leaving one nobody else
              works puts a person the agent has been talking to back in the pool for anyone —
              and an agent who expects the first and gets the second stops following up on
              someone who is now nobody's. */}
          <p className="text-xs">
            {outcome.kind === 'handover'
              ? tLog('desk_client_leave_confirm_shared', { name: others.map(o => o.name).filter(Boolean).join(', ') })
              : t('desk_client_leave_confirm_pool')}
          </p>
          <div className="flex items-center gap-2">
            <button type="button" className={btn('primary')} disabled={busy} onClick={onLeave}>
              {t('desk_client_leave_yes')}
            </button>
            <button type="button" className={btn('ghost')} disabled={busy}
              onClick={() => setConfirmLeave(false)}>
              {t('cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
